import { Card as MuiCard, CardContent, CardProps as MuiCardProps } from '@mui/material'
import { forwardRef } from 'react'

/**
 * Card component props
 */
export interface CardProps extends Omit<MuiCardProps, 'elevation'> {
  /** Card elevation (shadow depth) */
  elevation?: number
  /** Enable hover elevation effect */
  hover?: boolean
  /** Children content */
  children: React.ReactNode
  /** Disable padding on CardContent */
  noPadding?: boolean
}

/**
 * Custom Card component with elevation and hover effects
 * Extends Material UI Card with consistent styling
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ elevation = 1, hover = false, noPadding = false, children, sx, ...props }, ref) => {
    return (
      <MuiCard
        ref={ref}
        elevation={elevation}
        sx={{
          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
          ...(hover && {
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: (theme) => theme.shadows[elevation + 4] || theme.shadows[8],
            },
          }),
          ...sx,
        }}
        {...props}
      >
        <CardContent
          sx={{
            ...(noPadding && {
              padding: 0,
              '&:last-child': {
                paddingBottom: 0,
              },
            }),
          }}
        >
          {children}
        </CardContent>
      </MuiCard>
    )
  }
)

Card.displayName = 'Card'
